import React, { useState, useEffect } from 'react';
import { PageShell } from './layout';
import legalStyles from '../styles/Legal.module.css';
import { safeLocalStorageGetItem, safeLocalStorageSetItem } from '../utils/safeStorage';

const MondialiRegulations = () => {
  const [lang, setLang] = useState(() => {
    try {
      const stored = safeLocalStorageGetItem('policy-lang');
      if (stored === 'it' || stored === 'en') return stored;
    } catch (_) {}

    const navLangs = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || ''])
      .filter(Boolean)
      .map((l) => String(l).toLowerCase());
    const isItalian = navLangs.some((l) => l.startsWith('it'));
    return isItalian ? 'it' : 'en';
  });

  useEffect(() => {
    safeLocalStorageSetItem('policy-lang', lang);
  }, [lang]);

  const toggle = () => setLang((current) => (current === 'it' ? 'en' : 'it'));

  return (
    <PageShell showStickyAdRail={false}>
      <div style={{ maxWidth: 820, margin: '16px auto', padding: '0 16px', textAlign: 'justify' }} className={legalStyles.legalContent}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <button onClick={toggle} style={{ border: '1px solid #ddd', borderRadius: 20, padding: '6px 12px', background: '#fff', cursor: 'pointer' }}>
            {lang === 'it' ? 'English' : 'Italiano'}
          </button>
        </div>

        {lang === 'en' ? (
          <>
            <h1>ALMA Giochini Mondiali — Rules and Regulations</h1>
            <p><strong>Effective date:</strong> June 2, 2026</p>
            <p><strong>All dates and cutoffs in this page are in UTC.</strong></p>

            <h2>1) Scope and Roles</h2>
            <p>
              These rules govern participation in ALMA Giochini Mondiali on Giochini di Parole.
              Giochini di Parole administrators manage the platform and competition operations.
              Prize provisioning is handled by partners (ALMA Edizioni, , Teacher Stefano, Vaporetto Italiano, Italian Language Hub, Stivale Italiano, Online Italian Classes), as described below.
            </p>

            <h2>2) Competition Calendar (UTC)</h2>
            <ul>
              <li>Opening ceremony: <strong>June 11, 2026 to June 13, 2026</strong>.</li>
              <li>Competition weeks (5 weeks): <strong>June 14, 2026 to July 18, 2026</strong>.</li>
              <li>Closing ceremony: <strong>July 19, 2026</strong>.</li>
            </ul>

            <h2>3) Registration and Eligibility</h2>
            <ul>
              <li>Participation requires a registered Giochini di Parole account.</li>
              <li>At registration, each player selects a <strong>username</strong> and a <strong>country/team</strong>.</li>
              <li>Username and country/team selection are final after initial registration and cannot be changed.</li>
              <li>Each user may register for one country/team only.</li>
              <li>Registration is free.</li>
              <li>Prize eligibility for prizes furnished by ALMA Edizioni requires consent to share personal data with ALMA Edizioni at registration time for marketing purposes.</li>
              <li>Withdrawing this consent at any time makes the user ineligible to receive prizes.</li>
              <li>For prize-related processes, shared data includes <strong>name</strong> and <strong>email</strong>.</li>
            </ul>

            <h2>4) Gameplay and Stelline</h2>
            <ul>
              <li>Players earn stelline for each completed game.</li>
              <li>Extra stelline may be awarded to reward consistency, including frequency of play, frequency of hint usage, and frequency of use of Lella.</li>
              <li>Players may play all games available in the Giochini di Parole catalog.</li>
            </ul>

            <h2>5) Weekly Team Ranking</h2>
            <ul>
              <li>A country/team score is the average stelline of its top 11 players for the week.</li>
              <li>If a team has fewer than 11 players, the average is calculated on all available players in that team.</li>
              <li>Teams must have at least 5 players to be eligible for the weekly leaderboard.</li>
              <li>The ranking updates immediately whenever the page is refreshed.</li>
              <li>Each week ends on Saturday at <strong>23:59:59 UTC</strong>; then weekly standings reset.</li>
            </ul>

            <h2>6) Final Winner Determination</h2>
            <ol>
              <li>Each week, each team receives a placement (1st, 2nd, 3rd, etc.) based on that week&apos;s Top 11 average.</li>
              <li>After 5 weeks, each team&apos;s final score is the arithmetic mean of its five weekly placements.</li>
            </ol>
            <p><strong>Lower is better:</strong> the team with the lowest placement average wins the competition.</p>
            <p>
              This system rewards consistency over isolated peak performance.
              In case of ties or edge cases, organizers may apply additional consistency criteria and discretionary tie-breaking decisions.
              Such decisions are final and non-appealable.
            </p>

            <h2>7) Worked Example (5 Teams, 5 Weeks)</h2>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
              <thead>
                <tr>
                  <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '6px' }}>Team</th>
                  <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '6px' }}>W1</th>
                  <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '6px' }}>W2</th>
                  <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '6px' }}>W3</th>
                  <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '6px' }}>W4</th>
                  <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '6px' }}>W5</th>
                  <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '6px' }}>Placement Sum</th>
                  <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '6px' }}>Average</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>Italy</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>1</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>2</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>3</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>1</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>2</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>9</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>1.8</td>
                </tr>
                <tr>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>France</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>4</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>1</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>2</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>2</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>1</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>10</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>2.0</td>
                </tr>
                <tr>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>Spain</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>2</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>5</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>1</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>4</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>5</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>17</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>3.4</td>
                </tr>
                <tr>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>Germany</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>3</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>3</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>4</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>3</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>4</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>17</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>3.4</td>
                </tr>
                <tr>
                  <td style={{ padding: '6px' }}>Brazil</td>
                  <td style={{ padding: '6px' }}>5</td>
                  <td style={{ padding: '6px' }}>4</td>
                  <td style={{ padding: '6px' }}>5</td>
                  <td style={{ padding: '6px' }}>5</td>
                  <td style={{ padding: '6px' }}>3</td>
                  <td style={{ padding: '6px' }}>22</td>
                  <td style={{ padding: '6px' }}>4.4</td>
                </tr>
              </tbody>
            </table>
            <p>
              In this example, Italy wins because it maintained stronger weekly consistency.
              A single excellent week cannot compensate for multiple weak weeks in this system.
            </p>

            <h2>8) Prizes</h2>
            <ul>
              <li>Prizes are non-transferable.</li>
              <li>Weekly prizes are assigned for 5 weeks, from <strong>June 14, 2026 to July 18, 2026</strong>.</li>
              <li>Each week, 3 prizes are assigned to 3 different players: top stelline in the 1st-place team, top stelline in the 2nd-place team, and top stelline in the 3rd-place team.</li>
              <li>To reward broad team contribution, if a player has already won a weekly prize, that week&apos;s prize for the same team goes to the highest-ranked player who has not yet won a weekly prize.</li>
              <li>Final prizes are assigned to 3 different players: top stelline in the final 1st-place team, final 2nd-place team, and final 3rd-place team.</li>
              <li>Extra prizes are assigned to the top 3 players by total stelline overall during Mondiali.</li>
              <li>Extra prizes are assigned to the top 3 players from the winning team who played every day of the competition.</li>
              <li>To qualify for team prizes (weekly prizes, final prizes, and "stacanovisti" prizes), each team must have at least 5 players.</li>
              <li>Teams with fewer than 5 players are eligible only for prizes in the “Top player” ranking, to avoid awarding multiple prizes to the same person.</li>
              <li>Winners are contacted personally by email for prize collection instructions.</li>
            </ul>

            <h2>9) Prize Liability and Partner Fulfillment</h2>
            <p>
              Giochini di Parole administrators are not prize sponsors, issuers, or fulfillment providers, and are not liable for prize availability,
              quality, shipment, taxation, legal compliance, or delivery outcomes.
              Prizes are provided by partners, and final conferment and fulfillment are matters exclusively between each winner and the relevant partner provider.
            </p>

            <h2>10) Disqualification and Enforcement</h2>
            <ul>
              <li>Organizers may disqualify users for anomalous scores, suspected automation, cheating, abuse, policy violations, or any other reason at their discretion.</li>
              <li>Disqualification decisions may be made at any time during or after the competition.</li>
              <li>All organizer decisions are final and non-appealable.</li>
            </ul>
          </>
        ) : (
          <>
            <h1>ALMA Giochini Mondiali — Regole e Regolamenti</h1>
            <p><strong>Data di entrata in vigore:</strong> 2 giugno 2026</p>
            <p><strong>Tutte le date e le scadenze indicate in questa pagina sono in UTC.</strong></p>

            <h2>1) Ambito e Ruoli</h2>
            <p>
              Queste regole disciplinano la partecipazione ad ALMA Giochini Mondiali su Giochini di Parole.
              Gli amministratori di Giochini di Parole gestiscono la piattaforma e le operazioni della competizione.
              L&apos;erogazione dei premi è a carico dei partner (ALMA Edizioni, Teacher Stefano, Vaporetto Italiano, Italian Language Hub, Stivale Italiano, Online Italian Classes), come indicato di seguito.
            </p>

            <h2>2) Calendario della Competizione (UTC)</h2>
            <ul>
              <li>Cerimonia di apertura: <strong>11 giugno 2026 - 13 giugno 2026</strong>.</li>
              <li>Settimane di competizione (5 settimane): <strong>14 giugno 2026 - 18 luglio 2026</strong>.</li>
              <li>Cerimonia di chiusura: <strong>19 luglio 2026</strong>.</li>
            </ul>

            <h2>3) Registrazione e Idoneità</h2>
            <ul>
              <li>La partecipazione richiede un account registrato su Giochini di Parole.</li>
              <li>In fase di registrazione, ogni giocatore seleziona un <strong>nome utente</strong> e un <strong>Paese/squadra</strong>.</li>
              <li>La scelta di nome utente e Paese/squadra è definitiva dopo la registrazione iniziale e non può essere modificata.</li>
              <li>Ogni utente può registrarsi per un solo Paese/squadra.</li>
              <li>La registrazione è gratuita.</li>
              <li>L&apos;idoneità ai premi di ALMA Edizioni richiede il consenso, al momento della registrazione, alla condivisione dei dati personali con ALMA Edizioni per finalità di marketing.</li>
              <li>La revoca di tale consenso in qualsiasi momento rende l&apos;utente non idoneo alla ricezione di premi.</li>
              <li>Per i processi relativi ai premi, i dati condivisi sono <strong>nome</strong> ed <strong>email</strong>.</li>
            </ul>

            <h2>4) Partite e Stelline</h2>
            <ul>
              <li>Per ogni partita completata, i giocatori ricevono stelline.</li>
              <li>Possono essere assegnate stelline extra per premiare la costanza, inclusa la frequenza di gioco, la frequenza nell&apos;uso degli aiutini e la frequenza nell&apos;uso di Lella.</li>
              <li>I giocatori possono giocare tutte le partite disponibili nel catalogo di Giochini di Parole.</li>
            </ul>

            <h2>5) Classifica Settimanale delle Squadre</h2>
            <ul>
              <li>Il punteggio di un Paese/squadra è la media delle stelline dei migliori 11 giocatori della settimana.</li>
              <li>Se una squadra ha meno di 11 giocatori, la media è calcolata su tutti i giocatori disponibili di quella squadra.</li>
              <li>Le squadre devono avere almeno 5 giocatori per essere incluse nella classifica settimanale.</li>
              <li>La classifica si aggiorna istantaneamente ogni volta che si ricarica la pagina.</li>
              <li>Ogni settimana termina il sabato alle <strong>23:59:59 UTC</strong>; successivamente la classifica settimanale si azzera.</li>
            </ul>

            <h2>6) Determinazione della Squadra Vincitrice</h2>
            <ol>
              <li>Ogni settimana ogni squadra riceve un piazzamento (1°, 2°, 3°, ecc.) in base alla media Top 11 di quella settimana.</li>
              <li>Al termine delle 5 settimane, il punteggio finale di ogni squadra è la media aritmetica dei cinque piazzamenti settimanali.</li>
            </ol>
            <p><strong>L'indicatore più basso è il migliore:</strong> vince la competizione la squadra con la media piazzamenti più bassa.</p>
            <p>
              Questo sistema premia la costanza rispetto a prestazioni isolate.
              In caso di pari merito o casi limite, gli organizzatori possono applicare criteri aggiuntivi di costanza e decisioni discrezionali di spareggio.
              Tali decisioni sono finali e non impugnabili.
            </p>

            <h2>7) Esempio Pratico (5 Squadre, 5 Settimane)</h2>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
              <thead>
                <tr>
                  <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '6px' }}>Squadra</th>
                  <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '6px' }}>Sett. 1</th>
                  <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '6px' }}>Sett. 2</th>
                  <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '6px' }}>Sett. 3</th>
                  <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '6px' }}>Sett. 4</th>
                  <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '6px' }}>Sett. 5</th>
                  <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '6px' }}>Somma Piazz.</th>
                  <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '6px' }}>Media</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>Italia</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>1</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>2</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>3</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>1</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>2</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>9</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>1,8</td>
                </tr>
                <tr>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>Francia</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>4</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>1</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>2</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>2</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>1</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>10</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>2,0</td>
                </tr>
                <tr>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>Spagna</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>2</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>5</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>1</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>4</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>5</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>17</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>3,4</td>
                </tr>
                <tr>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>Germania</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>3</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>3</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>4</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>3</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>4</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>17</td>
                  <td style={{ borderBottom: '1px solid #eee', padding: '6px' }}>3,4</td>
                </tr>
                <tr>
                  <td style={{ padding: '6px' }}>Brasile</td>
                  <td style={{ padding: '6px' }}>5</td>
                  <td style={{ padding: '6px' }}>4</td>
                  <td style={{ padding: '6px' }}>5</td>
                  <td style={{ padding: '6px' }}>5</td>
                  <td style={{ padding: '6px' }}>3</td>
                  <td style={{ padding: '6px' }}>22</td>
                  <td style={{ padding: '6px' }}>4,4</td>
                </tr>
              </tbody>
            </table>
            <p>
              In questo esempio, l&apos;Italia vince grazie a una maggiore continuità settimanale.
              In questo sistema, una sola settimana eccellente non è sufficiente a compensare più settimane con performance scadenti.
            </p>

            <h2>8) Premi</h2>
            <ul>
              <li>I premi non sono trasferibili.</li>
              <li>I premi settimanali sono assegnati per 5 settimane, dal <strong>14 giugno 2026 al 18 luglio 2026</strong>.</li>
              <li>Ogni settimana sono assegnati 3 premi a 3 giocatori diversi: più stelline nella 1a squadra, più stelline nella 2a squadra e più stelline nella 3a squadra.</li>
              <li>Per valorizzare il contributo di tutti, se un giocatore ha già vinto un premio settimanale, il premio di quella squadra passa al primo giocatore utile in classifica che non abbia mai vinto un premio settimanale.</li>
              <li>Sono previsti 3 premi finali per 3 giocatori diversi: più stelline nella squadra 1a, nella squadra 2a e nella squadra 3a della classifica finale.</li>
              <li>Sono previsti premi extra per i 3 giocatori con più stelline in assoluto durante i Mondiali.</li>
              <li>Sono previsti premi extra per i 3 giocatori della squadra vincitrice che hanno giocato tutti i giorni della competizione.</li>
              <li>Per accedere ai premi di squadra (premi settimanali, premi finali e premi stacanovisti) ogni squadra deve avere almeno 5 giocatori.</li>
              <li>⁠Le squadre con meno di 5 giocatori hanno diritto solo ai premi previsti nella classifica “Top player” per evitare plurime assegnazioni dei premi alla stessa persona.</li>
              <li>I vincitori vengono contattati personalmente via email per le istruzioni di riscossione.</li>
            </ul>

            <h2>9) Responsabilità sui Premi e Ruolo dei Partner</h2>
            <p>
              Gli amministratori di Giochini di Parole non sono sponsor, emittenti o fornitori esecutivi dei premi,
              e non sono responsabili per disponibilità, qualità, spedizione, tassazione, conformità legale o esito della consegna.
              I premi sono forniti dai partner e l&apos;assegnazione finale e la relativa erogazione sono materie gestite esclusivamente tra ciascun vincitore e il partner fornitore competente.
            </p>

            <h2>10) Squalifica e Applicazione del Regolamento</h2>
            <ul>
              <li>Gli organizzatori possono squalificare utenti per punteggi anomali, sospetto di automazione, cheating, abusi, violazioni di policy o qualsiasi altro motivo a propria discrezione.</li>
              <li>Le squalifiche possono essere applicate in qualsiasi momento, durante o dopo la competizione.</li>
              <li>Tutte le decisioni degli organizzatori sono finali e non impugnabili.</li>
            </ul>
          </>
        )}
      </div>
    </PageShell>
  );
};

export default MondialiRegulations;
